import { shallowEqual } from 'react-redux'
import React, { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled/macro'
import { Button, Icon, Tooltip } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { useHistory } from 'react-router-dom'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import DashboardIcon from 'static/images/Dashboard_Icon.svg'
import AlertsIcon from 'static/images/Alerts_Icon.svg'
import BeaconsIcon from 'static/images/Beacons_Icon.svg'
import ButtonsDevicesIcon from 'static/images/Buttons_Devices_Icon.svg'
import RespondersIcon from 'static/images/Responders_icon.svg'
import UsersIcon from 'static/images/Users_Icon.svg'
import RouterIcon from '@material-ui/icons/RouterOutlined'
import RecordVoiceOverIcon from '@material-ui/icons/RecordVoiceOver'
import { RoundedTextField } from 'components/legacy-poorly-defined/Common'
import {
  DASHBOARD_MAIN_PATH,
  BEACONS_PATH,
  DEVICES_PATH,
  GRAY3_COLOR,
  USERS_PATH,
  RESPONDERS_PATH,
  ALERTS_PATH,
  ALL_ORGS_SELECTED,
  BORDER1_COLOR,
  GATEWAYS_PATH,
  CHANNELS_PATH,
  getAppColors,
  PRIMARY_COLOR,
} from 'lib/constants'
import { AlertSessions } from 'models'
import { ChartsSection } from './ChartsSection'
import { trans, TranslationGroup, TranslationKey } from 'lib/types/translation'
import { RenderCounter } from 'components/widgets/RenderCounter'
import { reloadAll } from 'models'
import Refresh from '../MainHeader/Refresh'
import { useGlobal } from 'contexts/GlobalProvider'
import { sumObjectValues } from 'lib/utils'
import { ReportStatus } from 'lib/types/reports'
import { getRequestV5 } from 'models/api/rest'
import { isVoiceEnabled } from 'lib/utils/auth'

const borderRadius = 10

const MainSidebarContainer = styled.div({
  width: '350px',
  position: 'fixed',
  overflowY: 'auto',

  '@media (max-width: 936px)': {
    justifyContent: 'space-evenly',
  },

  '@media (min-width: 1300px)': {
    height: '100%',
  },

  '@media (max-width: 1300px)': {
    background: getAppColors('--color-surface'),
    zIndex: 200,
    width: '100vw',
    position: 'fixed',
    top: '65px',
    borderBottom: `1px solid ${getAppColors('--color-gray-1')}`,
  },
})

const MainSidebarPaddingContainer = styled.div({
  padding: '25px 0 25px 25px',
  '@media (max-width: 1300px)': {
    padding: '0px 0 20px 20px',
  },
})

const RightCount = styled.div`
  margin-left: auto;
  @media (max-width: 1300px) {
    display: none;
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  &:first-child {
    border-radius: 10px;
  }

  button {
    font-family: Montserrat;
    background: ${GRAY3_COLOR};
    /* MUI's contained button text is near-black in both themes (it assumes a light grey fill) */
    color: ${getAppColors('--color-text')};
    height: 56px;
    width: 100%;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    justify-content: start;

    &:hover {
      box-shadow: none;
      background-color: ${getAppColors('--color-gray-4')};
    }
    &:first-child {
      border-radius: 0;
    }
    &:last-child {
      border-radius: 0 0 ${borderRadius}px ${borderRadius}px;
    }

    @media (max-width: 1100px) {
      font-size: 0;
      .MuiButton-startIcon {
        margin: 0;
      }
      min-width: 0;
    }

    @media (max-width: 1300px) {
      &:last-child {
        border-radius: 0 ${borderRadius}px ${borderRadius}px 0;
      }
    }

    &:only-child {
      border-radius: ${borderRadius}px;
    }
  }

  button.Mui-disabled {
    opacity: 0.45;
    background: ${getAppColors('--color-surface-2')};
    color: ${getAppColors('--color-text-disabled')};
  }

  @media (max-width: 1300px) {
    flex-direction: row;
    width: 85vw;
    & > button {
      width: unset;
    }
  }
`

const StyledRoundedTextField = styled(RoundedTextField)`
  .MuiInputBase-root fieldset {
    border: 0;
  }
  .MuiInputBase-root.MuiOutlinedInput-root.MuiAutocomplete-inputRoot.MuiInputBase-fullWidth.MuiInputBase-formControl.MuiInputBase-adornedEnd.MuiOutlinedInput-adornedEnd {
    background: ${GRAY3_COLOR};
    border-radius: ${borderRadius}px ${borderRadius}px 0 0;
    border-bottom: 1px solid ${getAppColors('--color-border')};
    @media (max-width: 1300px) {
      border-radius: ${borderRadius}px 0 0 ${borderRadius}px;
      border-bottom: 0px solid ${getAppColors('--color-border')};
      border-right: 1px solid ${getAppColors('--color-border')};
      width: 260px;
    }
  }
`

const SidebarSubElementContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',

  '@media (max-width: 1300px)': {
    width: '40vw',
    flexDirection: 'row',
  },
})

const Spacer = styled.div`
  display: inline-block;
  min-width: 15px;
  min-height: 15px;
`

const ChartsContainer = styled.div`
  @media (max-width: 1300px) {
    display: none;
  }
`

const StyledOrgName = styled.div`
  background: ${GRAY3_COLOR};
  border-radius: 10px 10px 0 0;
  border-bottom: 1px solid ${getAppColors('--color-border')};
  padding: 19px 16px;
`

const getIcon = (icon: any, alt: string) => {
  if (typeof icon === 'string') {
    return (
      <Icon>
        <img alt={alt} src={icon} style={{ marginBottom: '5px', height: '100%' }} />
      </Icon>
    )
  }
  return <Icon>{React.createElement(icon, { style: { color: PRIMARY_COLOR } })}</Icon>
}

type SidebarItem = {
  key: string
  name: string
  icon: any
  path: string
  tip: string
  count?: any
  disabled?: boolean
  disabledTip?: string
  hidden?: boolean
}

export const MainSidebar = () => {
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [showGateways, setShowGateways] = useState(false)

  const { selectOrg, selectedOrg, selectedOrgId, reportStatus, setReportStatus, navItems } = useGlobal()

  const translation: TranslationGroup = trans.merge(TranslationKey.MAIN_SIDEBAR)
  const DEFAULT_LOCATION = useMemo(
    () => ({ name: translation.all_properties, id: translation.all_properties }),
    [translation.all_properties],
  )

  // Cache the selected org's name so a reload can render it immediately, before
  // GlobalProvider finishes fetching the full org. Read once at mount.
  const persistedOrg = useMemo(() => {
    const id = localStorage.getItem('selected_org_id')
    if (!id || id === ALL_ORGS_SELECTED) return null
    return { id, name: localStorage.getItem('selected_org_name') || '' }
  }, [])

  // Keep the cached name in sync whenever the org resolves (from any entry
  // point: sidebar, deep link, notification, hydration).
  useEffect(() => {
    if (selectedOrg?.id && selectedOrg?.name) {
      localStorage.setItem('selected_org_name', selectedOrg.name)
    } else if (selectedOrgId === ALL_ORGS_SELECTED) {
      localStorage.removeItem('selected_org_name')
    }
  }, [selectedOrg, selectedOrgId])

  // Avoids the "All Properties" → property flicker: show the resolved org, else
  // the cached one, and only fall back to All Properties when that's the actual
  // selection.
  const orgDisplayValue = selectedOrg
    ? selectedOrg
    : selectedOrgId === ALL_ORGS_SELECTED
    ? DEFAULT_LOCATION
    : persistedOrg || DEFAULT_LOCATION

  const voiceEnabled = Boolean(selectedOrg && isVoiceEnabled(selectedOrg))

  useEffect(() => {
    if (!selectedOrg) {
      setShowGateways(true)
      return
    }
    if (reportStatus?.gateways) {
      const { critical = 0, moderate = 0, healthy = 0 } = reportStatus.gateways
      if (critical + moderate + healthy !== 0) {
        setShowGateways(true)
      } else {
        setShowGateways(false)
      }
    }
  }, [reportStatus, selectedOrg])

  const handleChange = (event: any, newValue: any) => {
    // Clearing the field (the ✕) or picking "All Properties" intentionally
    // switches to All Properties. `selectOrg` commits state and localStorage
    // together; the org fetch and /reports/status refetch follow from
    // GlobalProvider's effects on selectedOrgId.
    selectOrg(newValue?.id || ALL_ORGS_SELECTED)
  }

  const getSelectedStyle = (routePath: any): any =>
    history.location.pathname.match(routePath)
      ? { backgroundColor: getAppColors('--color-gray-4'), fontSize: '1.4rem' }
      : undefined

  const renderButton = (item: SidebarItem) => {
    if (item.hidden) return null

    const style = item.disabled ? undefined : getSelectedStyle(item.path)

    const button = (
      <Button
        aria-label={item.name}
        variant="contained"
        startIcon={getIcon(item.icon, item.name)}
        data-testid={`sidebar-${item.key}`}
        onClick={() => {
          if (!item.disabled) history.push(item.path)
        }}
        style={style}
        disabled={Boolean(item.disabled)}
      >
        {item.name}
        <RightCount>{item.count}</RightCount>
      </Button>
    )

    return (
      <Tooltip key={item.key} title={item.disabled ? item.disabledTip || item.tip : item.tip}>
        <span style={{ width: '100%' }}>{button}</span>
      </Tooltip>
    )
  }

  const sortOptionsAlphabetically = (options: any[]) => {
    return options.slice().sort((a, b) => a.name.localeCompare(b.name))
  }

  const sortedOptions = sortOptionsAlphabetically(navItems)

  const items: SidebarItem[] = [
    {
      key: 'Alerts',
      name: translation.alerts,
      icon: AlertsIcon,
      path: ALERTS_PATH,
      tip: translation.alerts_tooltip,
      count: loading ? '...' : sumObjectValues(reportStatus, 'alerts'),
    },
    {
      key: 'Devices',
      name: translation.devices,
      icon: ButtonsDevicesIcon,
      path: DEVICES_PATH,
      tip: translation.devices_tooltip,
      count: loading ? '...' : sumObjectValues(reportStatus, 'devices'),
    },
    {
      key: 'Responders',
      name: translation.responders,
      icon: RespondersIcon,
      path: RESPONDERS_PATH,
      tip: translation.responders_tooltip,
      count: loading ? '...' : sumObjectValues(reportStatus, 'responders'),
    },
    {
      key: 'Users',
      name: translation.users,
      icon: UsersIcon,
      path: USERS_PATH,
      tip: translation.users_tooltip,
      count: loading ? '...' : reportStatus ? reportStatus.users : 0,
    },
    {
      key: 'Beacons',
      name: translation.beacons,
      icon: BeaconsIcon,
      path: BEACONS_PATH,
      tip: translation.beacons_tooltip,
      count: loading ? '...' : sumObjectValues(reportStatus, 'beacons'),
    },
    ...(showGateways
      ? [
          {
            key: 'Gateways',
            name: 'Gateways',
            icon: RouterIcon,
            path: GATEWAYS_PATH,
            tip: translation.gateways_tooltip,
            count: loading ? '...' : sumObjectValues(reportStatus, 'gateways'),
          } as SidebarItem,
        ]
      : []),
    ...(selectedOrg && voiceEnabled
      ? [
          {
            key: 'Voice',
            name: 'Channels',
            icon: () => <RecordVoiceOverIcon style={{ color: PRIMARY_COLOR, fontSize: '20px' }} />,
            path: CHANNELS_PATH,
            tip: 'List of Voice Channels',
            count: loading ? '...' : reportStatus?.channels || '-',
          } as SidebarItem,
        ]
      : []),
  ]

  return (
    <MainSidebarContainer>
      <MainSidebarPaddingContainer>
        <SidebarSubElementContainer>
          <RenderCounter name={'MainSidebar'} />

          <ButtonsContainer>
            {renderButton({
              key: 'DashboardTop',
              name: translation.dashboard,
              icon: DashboardIcon,
              path: DASHBOARD_MAIN_PATH,
              tip: translation.dashboard_tooltip,
            })}
          </ButtonsContainer>

          <Spacer>&nbsp;</Spacer>

          {navItems.length === 1 ? (
            <StyledOrgName>{selectedOrg?.name || persistedOrg?.name || ''}</StyledOrgName>
          ) : (
            <Autocomplete
              id="organization"
              value={orgDisplayValue}
              onChange={handleChange}
              options={[DEFAULT_LOCATION, ...sortedOptions]}
              getOptionSelected={(option, value) => option?.id === value?.id}
              defaultValue={DEFAULT_LOCATION}
              getOptionLabel={option => option?.name}
              popupIcon={<KeyboardArrowDownIcon />}
              renderInput={params => <StyledRoundedTextField {...params} variant="outlined" />}
              ListboxProps={{ style: { maxHeight: 280 } }}
            />
          )}

          <ButtonsContainer>{items.filter(i => i.key !== 'DashboardTop').map(renderButton)}</ButtonsContainer>
        </SidebarSubElementContainer>

        <ChartsContainer>
          <ChartsSection currentPath={history.location.pathname} />
        </ChartsContainer>
      </MainSidebarPaddingContainer>
    </MainSidebarContainer>
  )
}

export default MainSidebar
